import React, { useState, useEffect, createContext, useContext } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { 
  ArrowRight, 
  ArrowLeft, 
  X, 
  Car, 
  Receipt, 
  Landmark, 
  BarChart3,
  Sparkles,
  CheckCircle2,
  PartyPopper
} from 'lucide-react';

// Onboarding context
const OnboardingContext = createContext(null);

export const useOnboarding = () => useContext(OnboardingContext);

const TOUR_STEPS = [
  {
    id: 'welcome',
    type: 'modal',
    title: 'Welcome to Zero Deficit Sync!',
    description: 'Let\'s take a quick tour to help you get started with tracking your expenses and maximizing your tax deductions.',
    icon: Sparkles,
  },
  {
    id: 'mileage',
    type: 'highlight',
    target: '[data-testid="nav-mileage-link"]',
    title: 'Track Your Mileage',
    description: 'Log business trips manually or use GPS auto-tracking. Save frequent routes for one-tap logging.',
    icon: Car,
    position: 'right',
  },
  {
    id: 'receipts',
    type: 'highlight',
    target: '[data-testid="nav-receipts-link"]',
    title: 'Scan Receipts',
    description: 'Upload or photograph receipts. Our AI extracts vendor, date, and amount automatically.',
    icon: Receipt,
    position: 'right',
  },
  {
    id: 'transactions',
    type: 'highlight',
    target: '[data-testid="nav-transactions-link"]',
    title: 'Categorize Transactions',
    description: 'Swipe to categorize expenses as business or personal. Connect your bank for automatic imports.',
    icon: Landmark,
    position: 'right',
  },
  {
    id: 'reports',
    type: 'highlight',
    target: '[data-testid="nav-reports-link"]',
    title: 'Generate Reports',
    description: 'Export your data for tax season. Generate Schedule C reports and mileage logs.',
    icon: BarChart3,
    position: 'right',
  },
  {
    id: 'complete',
    type: 'modal',
    title: 'You\'re All Set!',
    description: 'Start by logging your first trip or uploading a receipt. Your tax deductions are waiting to be discovered!',
    icon: PartyPopper,
  },
];

export function OnboardingProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(true);

  // Check if user should see onboarding
  useEffect(() => {
    if (isAuthenticated && user) {
      const completedKey = `onboarding_completed_${user.email || user.id}`;
      const completed = localStorage.getItem(completedKey);
      
      if (!completed) {
        // Small delay to let the app render first
        setTimeout(() => {
          setHasCompleted(false);
          setIsActive(true);
          setCurrentStep(0);
        }, 1000);
      }
    }
  }, [isAuthenticated, user]);

  const startTour = () => {
    setCurrentStep(0);
    setIsActive(true);
  };

  const nextStep = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const skipTour = () => {
    completeTour();
  };

  const completeTour = () => {
    setIsActive(false);
    setHasCompleted(true);
    if (user) {
      const completedKey = `onboarding_completed_${user.email || user.id}`;
      localStorage.setItem(completedKey, 'true');
    }
  };

  const step = TOUR_STEPS[currentStep];

  return (
    <OnboardingContext.Provider value={{ startTour, isActive, hasCompleted }}>
      {children}
      
      {/* Modal steps (welcome and complete) */}
      {isActive && step?.type === 'modal' && (
        <Dialog open={true} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-md" hideClose>
            <DialogHeader>
              <div className="mx-auto w-16 h-16 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center mb-4">
                {step.icon && <step.icon className="w-8 h-8 text-[hsl(var(--accent))]" />}
              </div>
              <DialogTitle className="text-center text-xl">
                {step.title}
              </DialogTitle>
              <DialogDescription className="text-center text-base">
                {step.description}
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex justify-center gap-3 pt-4">
              {currentStep === 0 ? (
                <>
                  <Button variant="ghost" onClick={skipTour}>
                    Skip Tour
                  </Button>
                  <Button 
                    className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90"
                    onClick={nextStep}
                  >
                    Let's Go
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              ) : (
                <Button 
                  className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 px-8"
                  onClick={completeTour}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Get Started
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Highlight steps */}
      {isActive && step?.type === 'highlight' && (
        <HighlightTooltip
          step={step}
          currentStep={currentStep}
          totalSteps={TOUR_STEPS.length}
          onNext={nextStep}
          onPrev={prevStep}
          onSkip={skipTour}
        />
      )}
    </OnboardingContext.Provider>
  );
}

function HighlightTooltip({ step, currentStep, totalSteps, onNext, onPrev, onSkip }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    const target = document.querySelector(step.target);
    if (target) {
      const rect = target.getBoundingClientRect();
      setTargetRect(rect);
      
      // Calculate tooltip position
      let top = rect.top + rect.height / 2;
      let left = rect.right + 16;
      
      // Adjust if near viewport edges
      if (left + 320 > window.innerWidth) {
        left = rect.left - 336;
      }
      if (top + 200 > window.innerHeight) {
        top = window.innerHeight - 220;
      }
      if (top < 20) {
        top = 20;
      }
      
      setPosition({ top, left });
      
      // Scroll element into view if needed
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [step.target]);

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[9998] bg-black/60" onClick={onSkip} />
      
      {/* Spotlight on target element */}
      {targetRect && (
        <div
          className="fixed z-[9999] rounded-lg ring-4 ring-[hsl(var(--accent))] ring-offset-2 ring-offset-background pointer-events-none"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
          }}
        />
      )}
      
      {/* Tooltip */}
      <div
        className="fixed z-[10000] w-80 bg-card border border-border rounded-xl shadow-2xl p-4 animate-in fade-in slide-in-from-left-2 duration-300"
        style={{ top: position.top - 80, left: position.left }}
      >
        {/* Progress indicator */}
        <div className="flex items-center gap-1.5 mb-3">
          {TOUR_STEPS.filter(s => s.type === 'highlight').map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                idx <= currentStep - 1 ? 'bg-[hsl(var(--accent))]' : 'bg-muted'
              }`}
            />
          ))}
        </div>
        
        {/* Content */}
        <div className="flex gap-3">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-[hsl(var(--accent))]/10 flex items-center justify-center">
            {step.icon && <step.icon className="w-5 h-5 text-[hsl(var(--accent))]" />}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm mb-1">{step.title}</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
          <button
            onClick={onSkip}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip tour
          </button>
          <div className="flex gap-2">
            {currentStep > 1 && (
              <Button size="sm" variant="ghost" onClick={onPrev}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <Button 
              size="sm" 
              className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90"
              onClick={onNext}
            >
              {currentStep === totalSteps - 2 ? 'Finish' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default OnboardingProvider;
